/**
 * /release-plan-template — SEO landing page targeting the "release plan
 * template" / "release planning template" / "software release plan
 * template" cluster.
 *
 * Differentiation angle: a release plan in a doc or a spreadsheet is a
 * snapshot that detaches from the upstream scope and specs the moment
 * they move — scope gets cut, a dependency slips, an owner changes, and
 * nobody syncs the plan. This page hands the reader the actual template
 * (the eight sections, spelled out) so a searcher gets something usable,
 * then shows that Draftlize turns each section into a structured card
 * linked to its dependencies, where the plan auto-flags stale when an
 * upstream decision or spec changes.
 *
 * English-only + inline (no i18n), mirroring DecisionLogTemplate.tsx and
 * the comparison pages. Chinese variant lives in ReleasePlanTemplateZh.tsx.
 */
import { MarketingShell } from './MarketingShell';

export function ReleasePlanTemplate() {
  return (
    <MarketingShell
      currentLocale="en"
      hreflangPath="/release-plan-template"
      documentTitle="Release Plan Template (That Tracks What Changed) · Draftlize"
      documentDescription="A release plan template you can copy — Release name & version, Scope (in / out), Timeline & milestones, Dependencies, Rollout plan, Rollback plan, Owners, Success criteria. The catch: a release planning template in a doc or spreadsheet detaches from your scope and specs the instant they move. In Draftlize every section is a structured card linked to its dependencies, and the plan auto-flags stale when an upstream decision changes."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Template · Release Planning</span>
          <h1 className="serif" style={{ fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1, margin: '12px 0 24px' }}>
            A release plan template<br/>
            <em>that tracks what changed.</em>
          </h1>
          <p className="lede">
            You came for a <em>release plan template</em> — the sections, the headings, something to paste into a doc before the next ship. It's below; take it. But know what you're getting: a software release plan is a snapshot, and it detaches from reality the instant the scope it was built on moves. Cut a feature, slip a dependency, swap an owner — nothing in a spreadsheet connects the plan back to the decisions and specs it assumed, so the plan quietly becomes a work of fiction the team stops trusting. Draftlize gives you the same eight sections — and makes each one a card linked to its dependencies, where the whole plan flags itself stale the moment an upstream decision changes.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">The template</span>
            <h2 className="serif">Eight sections. <em>Copy them anywhere.</em></h2>
          </div>
          <p>This is the whole release planning template. It works in a doc, a wiki, or a spreadsheet — it's the shape every shipping team converges on. Use it as-is. The next section is about what changes when each part stops being a static cell and becomes a card that knows what it depends on.</p>
        </div>

        <div
          style={{
            border: '1px solid var(--ink-0)',
            background: 'var(--paper-0)',
            fontFamily: "'Inter', sans-serif",
          }}
        >
          {[
            {
              k: 'Release name & version',
              hint: 'Required',
              body: 'The label and version number everyone references. "v2.3 — Billing revamp." One name, so a spec, a thread, and a standup all point at the same release instead of three vague descriptions of it.',
            },
            {
              k: 'Scope (in / out)',
              hint: 'The boundary',
              body: 'What ships in this release — and, just as load-bearing, what explicitly does not. The out-of-scope list is the field that prevents the most arguments. Every item in scope should trace back to a decision that put it there.',
            },
            {
              k: 'Timeline & milestones',
              hint: 'When',
              body: 'Code freeze, RC, staging, GA. The dates that gate the release and the checkpoints between now and shipped. The part that goes wrong first, because a milestone slips and the rest of the plan keeps the old date.',
            },
            {
              k: 'Dependencies',
              hint: 'What this rests on',
              body: 'The upstream specs, decisions, services, and teams this release assumes are ready. In a spreadsheet this is a list you forget to revisit. It is the section that decides whether the plan is still true a week from now.',
            },
            {
              k: 'Rollout plan',
              hint: 'How it ships',
              body: 'How the release reaches users — flag, canary, percentage ramp, region by region. Who gets it first, what you watch, and the gate that has to be green before the next step opens.',
            },
            {
              k: 'Rollback plan',
              hint: 'The undo',
              body: 'How you take it back when something breaks. The trigger that decides "roll back," the exact steps, and how long they take. The section nobody opens until 2am, which is precisely why it has to be written before then.',
            },
            {
              k: 'Owners',
              hint: 'Who',
              body: 'The release owner, plus the directly-responsible person for each surface. Not for blame — so that when a milestone slips or the rollback fires, there is one name to ask instead of a channel to shout into.',
            },
            {
              k: 'Success criteria',
              hint: 'Done means',
              body: 'How you know the release worked: the metrics, the thresholds, the absence of the regressions you feared. Decide this before you ship, or "success" becomes whatever happened to happen.',
            },
          ].map((f, i) => (
            <div
              key={f.k}
              style={{
                display: 'grid',
                gridTemplateColumns: '220px 1fr',
                gap: 24,
                padding: '20px 22px',
                borderTop: i === 0 ? 0 : '1px dashed var(--rule)',
                alignItems: 'baseline',
              }}
            >
              <div>
                <span
                  className="serif"
                  style={{ fontSize: 19, fontWeight: 500, color: 'var(--ink-0)', display: 'block', lineHeight: 1.2 }}
                >
                  {f.k}
                </span>
                <span
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 9.5,
                    letterSpacing: '.1em',
                    textTransform: 'uppercase',
                    color: 'var(--amber)',
                    fontWeight: 700,
                  }}
                >
                  {f.hint}
                </span>
              </div>
              <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.6, color: 'var(--ink-1)' }}>{f.body}</p>
            </div>
          ))}
        </div>

        <p
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 11,
            letterSpacing: '.06em',
            color: 'var(--ink-3)',
            textAlign: 'center',
            marginTop: 18,
          }}
        >
          That's the template. Below: why the plan detaches from reality the moment scope moves — and what we do about it.
        </p>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Why a release plan <em>goes stale.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">The plan and the scope drift apart</h3>
            <p>You build the plan from the scope and specs as they stand today. Then a decision cuts a feature, a spec changes shape, a dependency slips a week. The doc that captured all of it doesn't notice — a spreadsheet can't watch the decisions upstream of it move, so the plan keeps describing a release that no longer exists.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">The Dependencies section is a promise you'll break</h3>
            <p>You list what the release rests on — the upstream spec, the service that has to ship first, the team you're waiting on. Then one of those moves, and reconciling the plan is manual archaeology: re-check every dependency by memory. Nobody does it. The section that was supposed to keep the plan honest is the first to lie.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Approved is the high-water mark</h3>
            <p>The plan is most accurate the minute it's signed off, and never that accurate again. By ship day a real fraction of it is wrong — a stale date, a scope item that got cut, a rollback step for a service that was replaced — and you can't tell which fraction, so the team quietly stops trusting the plan and runs the release from memory instead.</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">The same template, <em>made live.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Each section becomes a card, not a cell</h3>
            <p>In Draftlize the template isn't a blank spreadsheet — it's typed cards with those exact sections: Release & version, Scope, Timeline, Dependencies, Rollout, Rollback, Owners, Success criteria. Structured, addressable by ID, and citable from a spec or a thread instead of buried in a tab nobody opens.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Upstream changes, the plan flags stale</h3>
            <p>Because Scope and Dependencies are real links and not lists of nouns, changing the decision a scope item rested on — or the spec a milestone assumed — turns the affected parts of the plan stale automatically, the way a build system invalidates everything downstream of a changed file. The promise the template made, the substrate keeps.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">An AI agent reads and writes it</h3>
            <p>Claude Code or Cursor, over MCP, reads every relevant decision and spec before it drafts or updates the release plan, and writes changes back into the same shape. The plan stops being a document approved once and forgotten, and becomes context that gets read — and reconciled against reality — on every turn.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          A release plan tells you what you intend to ship. It can't tell you when the scope it was built on quietly changed underneath it.
        </blockquote>
        <cite>Keep the eight sections. Let the substrate keep them current.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Common questions.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: 'What is a release plan?', a: 'A release plan states what you intend to ship in a given release and when — the version, the scope, the timeline, the dependencies, the rollout and rollback steps, the owners, and how you will know it succeeded. It turns a target date into a coordinated set of commitments the whole team can work from.' },
            { q: 'What is the difference between a release plan and a product roadmap?', a: 'A roadmap is the strategic view — themes and outcomes over quarters. A release plan is the tactical one — exactly what ships in the next release and how. The roadmap says where you are going; the release plan says what leaves the building next, with dates, dependencies, and a rollback path.' },
            { q: 'What should a release plan include?', a: 'The eight sections above: release and version, scope, timeline, dependencies, rollout, rollback, owners, and success criteria. The two people forget are dependencies (what has to be true first) and rollback (how you undo it) — which are exactly the parts you need when a release goes sideways.' },
            { q: 'How do you keep a release plan from going out of date?', a: 'A plan is most accurate the minute it is approved and drifts from there. Draftlize makes each section a card linked to the decisions and specs it rests on, so when the scope a milestone assumed changes, the affected part of the plan flags itself stale — instead of the team quietly abandoning it and running the release from memory.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.7, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Start your release plan <em>free with $5.</em></h2>
        </div>
        <div className="trust-strip">
          <span>New accounts get $5 free</span>
          <span>Pay only for what you use</span>
          <span>Balance never expires</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Take the template above, or spin up a project and let the agent build the plan from your existing decisions and specs — then keep every section, and everything it depends on, current as the release moves.
          </p>
          <a className="btn-primary" href="/">Start free with $5</a>
        </div>
      </section>
    </MarketingShell>
  );
}
